import React, { useMemo } from "react";

import { gruposDoPeriodo, somarGrupo, variacao } from "./periodos";
import { formatar, fmtPctSigned } from "./formatos";

// Matriz do Cotado × Realizado (PO1-3621): linhas = transportadoras / UFs /
// total, colunas = meses (ou trimestres, semestres, anos) + total do período;
// cada linha abre em uma linha por métrica do bloco. Cabeçalho agrupado,
// primeira coluna fixa e rolagem horizontal (molde do heatmap do Faturamento).
//
// A troca de quebra e de visão é só reagrupamento em memória: o payload já
// traz as três quebras e todos os meses.
//
// Props:
//  - linhas: linhas da quebra ({ chave, rotulo, regiao?, meses, anoAnterior })
//  - meses: ['YYYY-MM', ...] do período
//  - visao: mes | trimestre | semestre | ano
//  - metricas: METRICAS[bloco]
//  - compararAnoAnterior: mostra, sob cada célula, o mesmo período do ano anterior e a variação
//  - onCelula(linha, grupo, metrica, celula): clique numa célula clicável
export default function MatrizCotadoRealizado({ linhas, meses, visao, metricas, compararAnoAnterior, onCelula }) {
  const grupos = useMemo(() => gruposDoPeriodo(meses || [], visao || "mes"), [meses, visao]);
  const temParcial = grupos.some((g) => !g.completo) && visao !== "mes";

  if (!linhas || linhas.length === 0) {
    return <div className="cr-state">Nenhuma linha para o período e a quebra selecionados.</div>;
  }

  const renderCelula = (linha, grupo, metrica, idx) => {
    const celula = somarGrupo(linha.meses, grupo ? grupo.meses : meses);
    const valor = metrica.valor(celula);
    const vazio = valor === null || valor === undefined;
    const clicavel = Boolean(metrica.detalhe && onCelula && !vazio && Number(valor) !== 0);
    let anterior = null;
    let anteriorValor = null;
    if (compararAnoAnterior) {
      anterior = somarGrupo(linha.anoAnterior, grupo ? grupo.meses : meses);
      anteriorValor = anterior === null ? null : metrica.valor(anterior);
    }
    const varPct = compararAnoAnterior && !vazio && anteriorValor !== null && anteriorValor !== undefined ? variacao(valor, anteriorValor) : null;
    const classes = [
      grupo ? "" : "cr-col-total",
      clicavel ? "cr-celula--click" : "",
      vazio ? "cr-celula--vazia" : "",
      !vazio && Number(valor) !== 0 && metrica.tom ? `cr-celula--${metrica.tom}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <td
        key={grupo ? grupo.chave : "total"}
        className={classes}
        onClick={clicavel ? () => onCelula(linha, grupo, metrica, celula) : undefined}
        title={clicavel ? "Ver os documentos desta célula" : undefined}
      >
        {formatar(valor, metrica.formato)}
        {compararAnoAnterior ? (
          <span className="cr-ant">
            {anteriorValor === null || anteriorValor === undefined ? "ano ant.: —" : `ano ant.: ${formatar(anteriorValor, metrica.formato)}`}
            {varPct !== null ? (
              <span className={`cr-ant__var ${varPct > 0 ? "cr-ant__var--up" : varPct < 0 ? "cr-ant__var--down" : ""}`}>{fmtPctSigned(varPct)}</span>
            ) : null}
          </span>
        ) : null}
      </td>
    );
  };

  return (
    <>
      <div className="cr-matriz-wrap">
        <table className="cr-matriz">
          <thead>
            <tr>
              <th className="cr-col-linha">Linha / métrica</th>
              {grupos.map((g) => (
                <th key={g.chave} title={g.completo ? undefined : `Período parcial: ${g.meses.length} mês(es)`}>
                  {g.rotulo}
                  {!g.completo && visao !== "mes" ? <span className="cr-parcial">*</span> : null}
                </th>
              ))}
              <th className="cr-col-total">Período</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) =>
              metricas.map((metrica, i) => (
                <tr
                  key={`${linha.chave}-${metrica.key}`}
                  className={`${i === 0 ? "cr-linha-inicio" : ""}${metrica.destaque ? " cr-metrica-destaque" : ""}`}
                >
                  <td className="cr-col-linha">
                    {i === 0 ? (
                      <span className="cr-linha-nome">
                        {linha.rotulo}
                        {linha.regiao ? <span className="cr-linha-regiao">{linha.regiao}</span> : null}
                      </span>
                    ) : null}
                    <span className="cr-metrica">{metrica.label}</span>
                  </td>
                  {grupos.map((g) => renderCelula(linha, g, metrica))}
                  {renderCelula(linha, null, metrica)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {temParcial ? <p className="cr-footnote">* período parcial: o grupo não tem todos os meses dentro do recorte.</p> : null}
      {compararAnoAnterior ? (
        <p className="cr-footnote">
          "ano ant." é o mesmo período do ano anterior; "—" significa sem dado (não é zero). A variação compara a célula com ela mesma um ano antes.
        </p>
      ) : null}
    </>
  );
}
